import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import EmptyTable from "./EmptyTable";
import {
  MdDeleteOutline,
  MdLocalPrintshop,
  MdOutlineAttachMoney,
} from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import { useReactToPrint } from "react-to-print";
import { FiCalendar, FiExternalLink } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { LuFilter, LuFilterX } from "react-icons/lu";
import CurrencyInput from "../inputs/CurrencyInput";
import { useForm } from "react-hook-form";

const AdminPaymentsTableSpecific = ({ companyId, company, isActive }) => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);
  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypePayments, setSelectedTypePayments] = useState([]);
  const [openModalPaymentConfirm, setOpenModalPaymentConfirm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [errorPaymentConfirm, setErrorPaymentConfirm] = useState(false);
  const [errorPaymentDelete, setErrorPaymentDelete] = useState(false);
  const [openModalPaymentDelete, setOpenModalPaymentDelete] = useState(false);
  const [openModalPaymentReschedule, setOpenModalPaymentReschedule] =
    useState(false);
  const [rescheduleDueDate, setRescheduleDueDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState("");
  const [openManualModal, setOpenManualModal] = useState(false);
  const [manualDueDate, setManualDueDate] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const [manualDescription, setManualDescription] = useState("");
  const [manualAttachment, setManualAttachment] = useState(null);
  const [manualAttachmentKey, setManualAttachmentKey] = useState(0);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);
  const {
    control: manualControl,
    watch: watchManualFields,
    reset: resetManualFields,
  } = useForm({
    defaultValues: {
      manualAmount: "",
    },
  });
  const manualAmount = watchManualFields("manualAmount");
  const companyName =
    company?.name || company?.socialReason || company?.companyName || "Cliente";
  const companyAsaasId = company?.asaas_id || company?.asaasId || "";
  const manualAmountNumber = Number(manualAmount || 0);
  const canSubmitManual =
    !!manualDueDate && manualAmountNumber > 0 && !!companyAsaasId;

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const appliedFilterSummary = useMemo(() => {
    const items = [];
    if (startDate || endDate) {
      const startLabel = startDate
        ? moment(startDate).format("DD/MM/YYYY")
        : "—";
      const endLabel = endDate ? moment(endDate).format("DD/MM/YYYY") : "—";
      items.push(`Período: ${startLabel} a ${endLabel}`);
    }
    if (selectedStatuses.length > 0) {
      const labels = selectedStatuses
        .map((s) => (s === 1 ? "Pago" : "Pendente"))
        .join(", ");
      items.push(`Status: ${labels}`);
    }
    if (selectedTypePayments.length > 0) {
      items.push(`Tipo: ${selectedTypePayments.join(", ")}`);
    }
    return items;
  }, [startDate, endDate, selectedStatuses, selectedTypePayments]);
  const hasActiveFilters = appliedFilterSummary.length > 0;

  const {
    isLoading: isLoadingInvoices,
    error: errorInvoices,
    data: invoices,
  } = useQuery(
    [
      "invoices",
      startDate,
      endDate,
      selectedStatuses,
      selectedTypePayments,
      currentPage,
      companyId,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?startDate=${startDate}&endDate=${endDate}&status=${selectedStatuses}&typePayment=${selectedTypePayments}&page=${currentPage}&companyId=${companyId}`
      );

      setTotalPages(data.data.totalPages);
      setTotalCount(data.data.totalCount);
      setTotalPriceSum(data.data.totalPrice);

      return data.data.data;
    },
    { enabled: isActive }
  );

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const handleUpdatePayment = async (type) => {
    setErrorPaymentConfirm(false);
    try {
      await axios.get(
        `${ADMIN.UPDATE_PAYMENT}/${selectedInvoice?.id}?companyId=${
          type === "CONFIRM_UPDATE" ? selectedInvoice?.companyId : ""
        }`
      );

      setOpenModalPaymentConfirm(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (error) {
      setErrorPaymentConfirm(true);
    }
  };

  const handleDeletePayment = async () => {
    setErrorPaymentDelete(false);
    try {
      await axios.delete(`${ADMIN.DELETE_PAYMENT}/${selectedInvoice?.id}`);

      setOpenModalPaymentDelete(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (error) {
      setErrorPaymentDelete(true);
    }
  };

  const openRescheduleModal = (invoice) => {
    const currentDueDate = invoice?.dueDate
      ? moment(invoice.dueDate)
      : moment();
    const nextDueDate = currentDueDate.isAfter(moment(), "day")
      ? currentDueDate
      : moment();

    setSelectedInvoice(invoice);
    setRescheduleDueDate(nextDueDate.format("YYYY-MM-DD"));
    setRescheduleError("");
    setOpenModalPaymentReschedule(true);
  };

  const closeRescheduleModal = () => {
    setOpenModalPaymentReschedule(false);
    setRescheduleError("");
    setSelectedInvoice(null);
  };

  const handleReschedulePayment = async () => {
    setRescheduleError("");

    if (!selectedInvoice?.id) {
      setRescheduleError("Selecione uma cobrança para adiar.");
      return;
    }

    if (!rescheduleDueDate) {
      setRescheduleError("Informe a nova data de vencimento.");
      return;
    }

    try {
      setRescheduleLoading(true);
      await axios.put(
        `${ADMIN.RESCHEDULE_PAYMENT}/${selectedInvoice.id}`,
        {
          dueDate: rescheduleDueDate,
        },
      );

      closeRescheduleModal();
      queryClient.invalidateQueries(["invoices"]);
    } catch (error) {
      setRescheduleError(
        error?.response?.data?.message ||
          "Não foi possível adiar o vencimento.",
      );
    } finally {
      setRescheduleLoading(false);
    }
  };

  const resetManualModal = () => {
    setManualError("");
    setManualSuccess("");
    setManualDescription("");
    setManualAttachment(null);
    setManualAttachmentKey((current) => current + 1);
    setManualDueDate(moment().format("YYYY-MM-DD"));
    resetManualFields({ manualAmount: "" });
  };

  const closeManualModal = () => {
    setOpenManualModal(false);
    resetManualModal();
  };

  const openManualPaymentModal = () => {
    resetManualModal();
    setOpenManualModal(true);
  };

  const submitManualPayment = async () => {
    setManualError("");
    setManualSuccess("");

    if (!companyAsaasId) {
      setManualError(
        "Este cliente precisa ter o identificador do Asaas preenchido para gerar a cobrança.",
      );
      return;
    }

    const amount = Number(manualAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      setManualError("Informe um valor válido para a cobrança.");
      return;
    }

    try {
      setManualLoading(true);

      const companyPayload = {
        companyId: company?.id ?? companyId,
        id: company?.id ?? companyId,
        companyName,
        adjustedTotalPrice: amount,
        asaas_id: companyAsaasId,
      };

      if (manualAttachment) {
        const formData = new FormData();
        formData.append("dueDate", manualDueDate);
        formData.append("description", manualDescription || "");
        formData.append("companies", JSON.stringify([companyPayload]));
        formData.append("attachment", manualAttachment);

        await axios.post(`${ADMIN.RUN_CLOSING}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${ADMIN.RUN_CLOSING}`, {
          dueDate: manualDueDate,
          description: manualDescription || "",
          companies: [companyPayload],
        });
      }

      setManualSuccess("Cobrança criada com sucesso.");
      queryClient.invalidateQueries(["invoices"]);

      setTimeout(() => {
        setOpenManualModal(false);
        resetManualModal();
      }, 900);
    } catch (error) {
      setManualError(
        error?.response?.data?.message ||
          "Não foi possível criar a cobrança. Tente novamente.",
      );
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="bg-white text-slate-700 border border-slate-200 shadow-sm hover:bg-slate-50 transition-all duration-300 hover:text-white"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className="transition-transform duration-300">
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {renderFilters && (
              <div className="transition-opacity duration-500 min-w-[200px]">
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Filtros Avançados
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3">
                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Período de criação
                    </label>
                    <div className="flex items-center gap-2 h-9">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                      <div className="text-[11px] text-slate-500">até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                        }}
                        min={startDate}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Status
                    </label>
                    <div className="flex gap-2 h-9">
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(0, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(0)
                            ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pendente
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(1, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(1)
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pago
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Tipo de pagamento
                    </label>
                    <div className="flex gap-2 h-9">
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(
                            "BOLETO",
                            setSelectedTypePayments
                          )
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedTypePayments.includes("BOLETO")
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Boleto
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange("PIX", setSelectedTypePayments)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedTypePayments.includes("PIX")
                            ? "bg-emerald-700 text-white border-emerald-700 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pix
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 print-padding print-content" ref={reportRef}>
          <div className="mt-3">
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
                {hasActiveFilters ? (
                  <>
                    <span className="uppercase tracking-wide text-gray-500">
                      Filtros ativos
                    </span>
                    {appliedFilterSummary.slice(0, 4).map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                      >
                        {item}
                      </span>
                    ))}
                    {appliedFilterSummary.length > 4 && (
                      <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                        +{appliedFilterSummary.length - 4} oculto(s)
                      </span>
                    )}
                  </>
                ) : (
                  <span className="uppercase tracking-wide text-gray-500">
                    Nenhum filtro aplicado
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="flex gap-4 pb-3 min-w-[500px]">
              {/* Card - Valor Total */}
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Valor Total</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingInvoices ? (
                        formatToReal(totalPriceSum)
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-28" />
                      )}
                    </h2>
                  </div>
                  <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
                </div>
              </div>

              {/* Card - Total de Pagamentos */}
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Total de Pagamentos</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingInvoices ? (
                        totalCount
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                      )}
                    </h2>
                  </div>
                  <BsFillBarChartFill className="text-white text-4xl opacity-20" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 mt-2 flex flex-col md:flex-row justify-between items-end md:items-center gap-2">
            <div className="flex flex-col md:flex-row gap-2">
              <Button className="bg-gray-500" onClick={reactToPrintFn}>
                <div className="flex gap-2 items-center">
                  <div>Imprimir</div>
                  <MdLocalPrintshop fontSize={20} />
                </div>
              </Button>
              <Button
                color="success"
                onClick={openManualPaymentModal}
                disabled={!companyAsaasId}
                title={
                  !companyAsaasId
                    ? "Identificador do Asaas não informado para este cliente"
                    : undefined
                }
              >
                <div className="flex gap-2 items-center">
                  <div>Gerar cobrança manual</div>
                  <MdOutlineAttachMoney fontSize={20} />
                </div>
              </Button>
            </div>
          </div>

          <small
            className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
            style={{ fontSize: 10 }}
          >
            <span>
              Deslize para o lado para visualizar todas as informações
            </span>{" "}
            <FaArrowsLeftRight />
          </small>
          {invoices?.length > 0 || isLoadingInvoices ? (
            <div className="overflow-x-auto shadow-sm rounded-lg mt-0 md:mt-5">
              <Table striped style={{ fontSize: "13px" }}>
                <Table.Head>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Data de criação
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Data de pgto.
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Valor
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Tipo
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Status
                  </Table.HeadCell>
                  <Table.HeadCell className="text-center bg-gray-300">
                    Ações
                  </Table.HeadCell>
                </Table.Head>

                <Table.Body className="divide-y">
                  {!isLoadingInvoices ? (
                    <>
                      {invoices?.length > 0 && (
                        <>
                          {invoices.map((invoice, index) => (
                            (() => {
                              const isPaid =
                                invoice.isPaid === true ||
                                invoice.isPaid === 1 ||
                                invoice.isPaid === "1";
                              const isOverdue =
                                !isPaid &&
                                invoice?.dueDate &&
                                moment(invoice.dueDate)
                                  .startOf("day")
                                  .isBefore(moment().startOf("day"));

                              return (
                            <Table.Row
                              key={index}
                              className="bg-white dark:border-gray-700 dark:bg-gray-800"
                            >
                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-3">
                                {moment(invoice.createdAt).format("DD/MM/yyyy")}
                              </Table.Cell>
                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                    isOverdue
                                      ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                                      : "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
                                  }`}
                                >
                                  {invoice.dueDate
                                    ? moment(invoice.dueDate).format(
                                        "DD/MM/yyyy",
                                      )
                                    : "-"}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                {formatToReal(invoice?.price)}
                              </Table.Cell>
                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                {invoice.type}
                              </Table.Cell>
                              <Table.Cell className="text-center py-2">
                                {isPaid ? (
                                  <Badge
                                    color="success"
                                    className="w-fit mx-auto text-xs"
                                  >
                                    Pago
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="warning"
                                    className="w-fit mx-auto text-xs"
                                  >
                                    Pendente
                                  </Badge>
                                )}
                              </Table.Cell>
                              <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                <div className="flex justify-center gap-2">
                                  {invoice.paymentLink && (
                                    <div>
                                      <Tooltip
                                        id="tooltip-link"
                                        content="Link de pagamento"
                                      />
                                      <a
                                        data-tooltip-id="tooltip-link"
                                        className="mx-auto inline-block text-center"
                                        href={invoice.paymentLink}
                                        target="_blank"
                                      >
                                        <FiExternalLink fontSize={18} />
                                      </a>
                                    </div>
                                  )}

                                  {!isPaid && (
                                    <div>
                                      <Tooltip
                                        id="tooltip-reschedule-payment"
                                        content="Adiar vencimento"
                                      />
                                      <button
                                        type="button"
                                        data-tooltip-id="tooltip-reschedule-payment"
                                        className="mx-auto inline-flex items-center justify-center rounded-md bg-amber-100 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-200 hover:ring-amber-300 transition p-2"
                                        onClick={() => {
                                          openRescheduleModal(invoice);
                                        }}
                                      >
                                        <FiCalendar fontSize={16} />
                                      </button>
                                    </div>
                                  )}

                                  {invoice.type === "BOLETO" && !isPaid && (
                                      <div>
                                        <Tooltip
                                          id="tooltip-paid"
                                          content="Sinalizar como pago"
                                        />
                                        <a
                                          data-tooltip-id="tooltip-paid"
                                          className="mx-auto inline-block text-center cursor-pointer"
                                          onClick={() => {
                                            setSelectedInvoice(invoice);
                                            setOpenModalPaymentConfirm(true);
                                          }}
                                        >
                                          <IoMdCheckmarkCircleOutline
                                            className="text-green-500"
                                            fontSize={18}
                                          />
                                        </a>
                                      </div>
                                    )}

                                  {!isPaid && (
                                    <div>
                                      <Tooltip
                                        id="tooltip-delete-payment"
                                        content="Excluir cobrança"
                                      />
                                      <a
                                        data-tooltip-id="tooltip-delete-payment"
                                        className="mx-auto inline-block text-center cursor-pointer"
                                        onClick={() => {
                                          setSelectedInvoice(invoice);
                                          setOpenModalPaymentDelete(true);
                                        }}
                                      >
                                        <MdDeleteOutline
                                          className="text-red-500"
                                          fontSize={18}
                                        />
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                              );
                            })()
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {Array.from({ length: 12 }).map((_, index) => (
                        <Table.Row
                          key={index}
                          className="bg-white dark:border-gray-700 dark:bg-gray-800"
                        >
                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>

                          <Table.Cell className="font-medium text-gray-900 dark:text-white text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full dark:bg-gray-700 w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </>
                  )}
                </Table.Body>
              </Table>
            </div>
          ) : (
            <EmptyTable icon="search_off">
              Nenhum registro encontrado com esses filtros
            </EmptyTable>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>

        <Modal
          show={openModalPaymentConfirm}
          size="lg"
          onClose={() => setOpenModalPaymentConfirm(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center">
              <h3 className="mb-2 text-lg font-normal text-gray-500 dark:text-gray-500">
                Deseja sinalizar este boleto como pago?
              </h3>
              <p className="text-sm text-gray-500">
                Para dar baixa e desbloquear o cliente, clique no botão{" "}
                <b>"Confirmar e Desbloquear"</b>
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-2 mt-5">
                <Button
                  color="success"
                  onClick={() => handleUpdatePayment("CONFIRM")}
                >
                  Apenas confirmar
                </Button>

                <Button
                  color="gray"
                  onClick={() => handleUpdatePayment("CONFIRM_UPDATE")}
                >
                  Confirmar e desbloquear
                </Button>
              </div>
              {errorPaymentConfirm && (
                <Alert className="mt-4" color="failure" withBorderAccent>
                  Ops! Ocorreu algum erro, tente novamente mais tarde!
                </Alert>
              )}
            </div>
          </ModalBody>
        </Modal>

        <Modal
          show={openModalPaymentDelete}
          size="md"
          onClose={() => setOpenModalPaymentDelete(false)}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="text-center">
              <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
              <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                Tem certeza que deseja excluir essa cobrança?
              </h3>
              <div className="flex justify-center gap-2">
                <Button color="failure" onClick={() => handleDeletePayment()}>
                  Excluir
                </Button>
                <Button
                  color="gray"
                  onClick={() => setOpenModalPaymentDelete(false)}
                >
                  Cancelar
                </Button>
              </div>
              {errorPaymentDelete && (
                <Alert className="mt-4" color="failure" withBorderAccent>
                  Ops! Ocorreu algum erro, tente novamente mais tarde!
                </Alert>
              )}
            </div>
          </ModalBody>
        </Modal>

        <Modal
          show={openModalPaymentReschedule}
          size="lg"
          onClose={closeRescheduleModal}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-900">
                  Adiar vencimento
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedInvoice?.companyName || "Cobrança"}
                </p>
              </div>

              <Alert color="warning" withBorderAccent>
                A data será alterada apenas no sistema. O gateway de pagamento
                não será atualizado.
              </Alert>

              <div>
                <Label className="mb-1 block">Nova data de vencimento *</Label>
                <input
                  type="date"
                  value={rescheduleDueDate}
                  min={moment().format("YYYY-MM-DD")}
                  onChange={(e) => setRescheduleDueDate(e.target.value)}
                  className="w-full h-10 bg-white border border-slate-300 text-sm rounded-md px-3 focus:ring-slate-500 focus:border-slate-500"
                />
                {selectedInvoice?.dueDate && (
                  <div className="mt-2 text-xs text-slate-500">
                    Vencimento atual:{" "}
                    <span className="font-medium">
                      {moment(selectedInvoice.dueDate).format("DD/MM/YYYY")}
                    </span>
                  </div>
                )}
              </div>

              {rescheduleError && (
                <Alert color="failure" withBorderAccent>
                  {rescheduleError}
                </Alert>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button color="gray" onClick={closeRescheduleModal}>
                  Cancelar
                </Button>
                <Button
                  color="warning"
                  isProcessing={rescheduleLoading}
                  processingSpinner={<Spinner size="sm" />}
                  onClick={handleReschedulePayment}
                  disabled={rescheduleLoading || !rescheduleDueDate}
                >
                  Adiar vencimento
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          show={openManualModal}
          size="4xl"
          onClose={closeManualModal}
          popup
        >
          <ModalHeader />
          <ModalBody>
            <div className="space-y-5">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">
                  Criar cobrança manual
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Cliente: <span className="font-medium">{companyName}</span>
                </p>
              </div>

              {!companyAsaasId && (
                <Alert color="warning">
                  Este cliente não tem identificador do Asaas cadastrado.
                </Alert>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="mb-1 block">
                        Data de vencimento *
                      </Label>
                      <input
                        type="date"
                        value={manualDueDate}
                        onChange={(e) => setManualDueDate(e.target.value)}
                        min={moment().format("YYYY-MM-DD")}
                        className="w-full h-10 bg-white border border-slate-300 text-sm rounded-md px-3 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                    <div>
                      <CurrencyInput
                        control={manualControl}
                        name="manualAmount"
                        label="Valor da cobrança (R$) *"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="mb-1 block">
                      Descrição <span className="font-normal">(opcional)</span>
                    </Label>
                    <textarea
                      rows={3}
                      maxLength={500}
                      value={manualDescription}
                      onChange={(e) => setManualDescription(e.target.value)}
                      placeholder="Ex.: Cobrança complementar referente a ..."
                      className="w-full block py-2 px-3 rounded-md text-sm text-slate-700 bg-white border border-slate-300 focus:ring-slate-500 focus:border-slate-500"
                    />
                    <div className="text-right text-xs text-slate-500 mt-1">
                      {manualDescription.length} / 500 caracteres
                    </div>
                  </div>

                  <div>
                    <Label className="mb-1 block">Anexo (opcional)</Label>
                    <input
                      key={manualAttachmentKey}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) =>
                        setManualAttachment(e.target.files?.[0] || null)
                      }
                      className="w-full block py-2 px-3 rounded-md text-sm text-slate-700 bg-white border border-slate-300 focus:ring-slate-500 focus:border-slate-500"
                    />
                    {manualAttachment && (
                      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-600">
                        <span className="truncate">{manualAttachment.name}</span>
                        <Button
                          color="light"
                          size="xs"
                          onClick={() => {
                            setManualAttachment(null);
                            setManualAttachmentKey((current) => current + 1);
                          }}
                        >
                          Remover
                        </Button>
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-1">
                      PDF, PNG ou JPG.
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">
                      Resumo
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-900">
                      {companyName}
                    </div>
                    <div className="mt-3 text-xs text-slate-500">Total</div>
                    <div className="text-lg font-semibold text-slate-900">
                      {formatToReal(manualAmountNumber)}
                    </div>
                  </div>

                  {manualSuccess && (
                    <Alert color="success" withBorderAccent>
                      {manualSuccess}
                    </Alert>
                  )}
                </div>
              </div>

              {manualError && (
                <Alert color="failure" withBorderAccent>
                  {manualError}
                </Alert>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button color="gray" onClick={closeManualModal}>
                  Cancelar
                </Button>
                <Button
                  color="success"
                  isProcessing={manualLoading}
                  processingSpinner={<Spinner size="sm" />}
                  onClick={submitManualPayment}
                  disabled={!canSubmitManual || manualLoading}
                >
                  Criar cobrança
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

export default AdminPaymentsTableSpecific;
